// Shared vocabulary for the SM-504 deposit/withdrawal hierarchy.
//
// The screen's whole job is to keep two things apart that look alike:
// a route that is SHUT and a route that is LOCKED, and a value that is
// INHERITED and one that is OVERRIDDEN. Everything here exists to make
// those four states read differently at a glance.
import type {
  RailBlock,
  RailDirection,
  RailLevel,
  RailPolicy,
  RailResolved,
} from "@/lib/wallet-types";
import { Badge } from "@/components/ui/Badge";

export const DIRECTIONS: RailDirection[] = ["DEPOSIT", "WITHDRAW"];

/**
 * The direction picker's options, written out rather than derived.
 * A derived label would put the enum VALUE inside a UI attribute, and
 * the copy extractor would then demand a Thai translation of the string
 * "DEPOSIT" — an identifier, not a word.
 */
export const DIRECTION_OPTIONS: { value: RailDirection; label: string }[] = [
  { value: "DEPOSIT", label: "Deposits" },
  { value: "WITHDRAW", label: "Withdrawals" },
];

/** The plural noun for a direction, for use inside a sentence. */
export function directionNoun(direction: RailDirection): string {
  return DIRECTION_OPTIONS.find((d) => d.value === direction)?.label ?? "";
}

/** Display order for the three fixed platform currencies. */
export const RAIL_CURRENCIES = ["RED", "GREEN", "GOLD"] as const;

export const MODES = ["GAME", "CASH"] as const;

/** Which mode owns a currency: GAME debits RED and credits GREEN, CASH pays GOLD. */
export function modeForCurrency(currency: string): (typeof MODES)[number] {
  return currency === "GOLD" ? "CASH" : "GAME";
}

export function railKey(currency: string, direction: RailDirection): string {
  return `${currency}|${direction}`;
}

const LEVEL_LABELS: Record<RailLevel, string> = {
  PLATFORM: "platform",
  LEDGER: "wallet policy",
  RAIL: "crypto rail",
  MODE: "mode",
  CURRENCY: "currency",
  ASSET: "asset",
  ASSET_CURRENCY: "asset + currency",
};

export function levelLabel(level: RailLevel | ""): string {
  return level ? LEVEL_LABELS[level] : "default";
}

// Every reason the resolver can return, in the operator's language.
//
// The LEDGER_* codes are ledger-service's, passed through the rail
// verbatim (SM-514) rather than re-coded here: the wallet screen shows
// the same code to the player, and two names for one wall is how a
// support conversation goes wrong. What differs is the audience, so
// the wording here says WHERE the switch is. This page does not own the
// ledger rung, but the console does again (SM-514 rd2) — so the text
// names the page that holds it instead of stopping at "not here", which
// is a dead end dressed up as an explanation.
const REASON_TEXT: Record<string, string> = {
  CWALLET_RAIL_CEILING: "permanently unavailable — a capability ceiling, not a setting",
  CWALLET_RAIL_FLAG_OFF: "the crypto rail's master switch is off",
  CWALLET_RAIL_DISABLED: "switched off at this level",
  CWALLET_RAIL_MAINTENANCE: "in maintenance",
  CWALLET_RAIL_NO_RATE: "no active rate prices this route — set one under Asset Rates",
  CWALLET_RAIL_LEDGER_UNKNOWN: "the wallet policy service could not be reached, so the route is held shut",
  LEDGER_000304: "permanently unavailable — a capability ceiling, not a setting",
  LEDGER_000305: "switched off in the ledger currency registry — turn it on under Treasury → Currencies",
  LEDGER_000306: "the rollout flag for this rail is off — it is set on the deployment, not in this console",
  LEDGER_000307: "no ready provider for this rail",
};

/** Codes that name a rule rather than a switch, so "(level)" adds nothing. */
const CEILING_REASONS = new Set(["CWALLET_RAIL_CEILING", "LEDGER_000304"]);

/**
 * Every refusing level on a route, `blocks` first and `blocked_at` as
 * the one-element fallback for an upstream that only sends the first.
 */
export function railBlocks(resolved: RailResolved): RailBlock[] {
  if (resolved.blocks?.length) return resolved.blocks;
  return resolved.blocked_at ? [resolved.blocked_at] : [];
}

/**
 * The ceiling refusing this route, if any — the ONE predicate for
 * "locked", so the pill, the block list and the matrix filter can never
 * drift apart again.
 *
 * Two bugs lived in the gap between three copies of this question.
 * `railMatrix.isLocked` knew only `CWALLET_RAIL_CEILING` and not the
 * ledger's `LEDGER_000304`, so the Effective filter sorted a
 * ledger-ceilinged route under "Blocked"; and both it and the pill read
 * only `blocked_at`, which is the FIRST refusing level and not
 * necessarily the ceiling — a master switch off above a ceilinged
 * currency rendered "Blocked", sending an operator to hunt for a switch
 * that has never existed.
 */
export function ceilingBlock(resolved?: RailResolved): RailBlock | undefined {
  if (!resolved) return undefined;
  return railBlocks(resolved).find((b) => CEILING_REASONS.has(b.reason));
}

export function blockText(block: RailBlock): string {
  const reason = REASON_TEXT[block.reason] ?? block.reason;
  if (CEILING_REASONS.has(block.reason)) return reason;
  return `${reason} (${levelLabel(block.level)})`;
}

/**
 * The effective-state pill. A ceiling block reads "Locked" rather than
 * "Off" on purpose: an operator who sees "Off" goes looking for the
 * switch, and for RED withdrawals there has never been one.
 */
export function RailStatePill({ resolved }: { resolved?: RailResolved }) {
  if (!resolved) return <Badge tone="muted">—</Badge>;
  if (resolved.enabled) return <Badge tone="success">Open</Badge>;
  // Any ceiling anywhere in the list, not just the first refusing level:
  // a ceiling is permanent whatever else refused ahead of it.
  if (ceilingBlock(resolved)) return <Badge tone="muted">Locked</Badge>;
  if (resolved.blocked_at?.reason === "CWALLET_RAIL_MAINTENANCE") {
    return <Badge tone="warning">Maintenance</Badge>;
  }
  return <Badge tone="danger">Blocked</Badge>;
}

/**
 * Why a route is shut — EVERY refusing level, not just the first.
 * Showing only the first turns one fix into four round trips, because
 * each save reveals the next switch that was already off.
 *
 * That argument is about SWITCHES, and it stops at a ceiling. Beneath a
 * ceiling no number of flips opens the route, so listing the switches
 * that also refuse invites an operator to flip them and watch nothing
 * happen — the same mistake as labelling a ceiling "Off", which the
 * pill above already refuses to make. A ceilinged route names its
 * ceiling and nothing else.
 *
 * Identical sentences are collapsed. Two ceilings — the rail's own and
 * the ledger's — render the same text because `blockText` drops the
 * level for a rule, and RED withdrawals were printing "permanently
 * unavailable" twice in a row, which reads as a rendering fault rather
 * than as two services agreeing.
 */
export function BlockedBecause({ resolved }: { resolved?: RailResolved }) {
  if (!resolved || resolved.enabled) return null;
  const ceiling = ceilingBlock(resolved);
  const blocks = ceiling ? [ceiling] : railBlocks(resolved);
  const lines: string[] = [];
  for (const b of blocks) {
    const text = blockText(b);
    if (!lines.includes(text)) lines.push(text);
  }
  if (lines.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-2xs text-t-3">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/** An empty policy — every field inherited. */
export function emptyRailPolicy(): RailPolicy {
  return {
    fee_flat_minor: null,
    fee_bps: null,
    fee_min_minor: null,
    fee_max_minor: null,
    min_amount_minor: null,
    max_amount_minor: null,
    daily_max_minor: null,
    confirmations: null,
    rate_source: null,
    rate_spread_bps: null,
    rate_max_age_secs: null,
    maintenance: null,
  };
}

/** The policy fields an operator edits, in the order they are shown. */
export const POLICY_FIELDS: {
  key: keyof RailPolicy;
  label: string;
  hint: string;
  kind: "spark" | "bps" | "count" | "text" | "bool";
  /** Directions this field means anything for. */
  directions: RailDirection[];
}[] = [
  {
    key: "fee_flat_minor",
    label: "Flat fee",
    hint: "Charged on every movement, in the platform currency.",
    kind: "spark",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "fee_bps",
    label: "Percentage fee",
    hint: "Basis points of the amount. 100 = 1%.",
    kind: "bps",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "fee_min_minor",
    label: "Minimum fee",
    hint: "Floor applied after the flat and percentage parts. 0 = no floor.",
    kind: "spark",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "fee_max_minor",
    label: "Fee cap",
    hint: "Ceiling applied after the flat and percentage parts. 0 = no cap.",
    kind: "spark",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "min_amount_minor",
    label: "Minimum amount",
    hint: "Per transaction. 0 = no minimum.",
    kind: "spark",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "max_amount_minor",
    label: "Maximum amount",
    hint: "Per transaction. 0 = no maximum.",
    kind: "spark",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "daily_max_minor",
    label: "Daily cap",
    hint: "Total per user per day. 0 = no cap.",
    kind: "spark",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "confirmations",
    label: "Confirmations",
    hint: "Block depth required before a deposit is credited.",
    kind: "count",
    directions: ["DEPOSIT"],
  },
  {
    key: "rate_source",
    label: "Rate source",
    hint: "Which feed prices this route.",
    kind: "text",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "rate_spread_bps",
    label: "Rate spread",
    hint: "Markup taken on the source rate, in basis points.",
    kind: "bps",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "rate_max_age_secs",
    label: "Rate staleness limit",
    hint: "Seconds a quote stays usable. 0 = never stale.",
    kind: "count",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
  {
    key: "maintenance",
    label: "Maintenance",
    hint: "Temporarily closes the route without changing what it is configured to be.",
    kind: "bool",
    directions: ["DEPOSIT", "WITHDRAW"],
  },
];
